// Usos da IA do usuário fora das Publicações: ler a foto de um documento e organizar uma caixa de anotações.
import type { Box, Documento } from '../store/types';
import type { Campo } from '../modulos/tipos';
import { TIPOS_DOC } from '../store/documentos';
import { perguntarJSON, idiomaResposta, type Imagem } from './ia';
import { fusoAparelho } from './fuso';
import { today } from './date';

const okData = (s: any) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);
const okHora = (s: any) => typeof s === 'string' && /^\d{2}:\d{2}$/.test(s);

/** Lê a foto (ou PDF) de um documento e devolve os campos da ficha. */
export async function lerDocumento(img: Imagem): Promise<Partial<Documento>> {
  const tipos = TIPOS_DOC.map(x => x[0]);
  const r = await perguntarJSON<any>(
    `Você lê fotos de documentos pessoais e empresariais (de qualquer país) e preenche uma ficha. Não invente: deixe "" ou null o que não estiver legível.
Formato: {"tipo": um destes valores exatamente: ${JSON.stringify(tipos)},
 "nome":"nome curto do documento em ${idiomaResposta()}", "titular":"nome da pessoa ou empresa", "numero":"número principal do documento",
 "orgao":"órgão emissor", "emissao":"AAAA-MM-DD" ou null, "validade":"AAAA-MM-DD" ou null}`,
    'Leia o documento da imagem.', [img]);
  return {
    tipo: tipos.includes(r.tipo) ? r.tipo : undefined,
    nome: String(r.nome ?? '').slice(0, 80), titular: String(r.titular ?? '').slice(0, 120), numero: String(r.numero ?? '').slice(0, 60),
    orgao: String(r.orgao ?? '').slice(0, 80), emissao: okData(r.emissao) ? r.emissao : null, validade: okData(r.validade) ? r.validade : null,
  };
}

export type TarefaSugerida = { titulo: string; data: string | null; hora: string | null; duracao: number; energia: number; notas: string };

/** Transforma o conteúdo de uma caixa de anotações em tarefas sugeridas (com data e hora quando houver). */
export async function organizarCaixa(b: Box): Promise<TarefaSugerida[]> {
  const conteudo = [b.title, ...b.blocks.flatMap(bl => (bl.type === 'text' ? [bl.text] : bl.items.map(i => `${i.done ? '[x]' : '[ ]'} ${i.text}`)))]
    .filter(x => x.trim()).join('\n');
  const r = await perguntarJSON<any>(
    `Você ajuda a organizar a rotina. Hoje é ${today()} (fuso ${fusoAparelho()}). Transforme as anotações em tarefas práticas para uma agenda.
Junte itens que são a mesma coisa, ignore itens já marcados com [x], não invente compromissos. Use data/hora só se as anotações indicarem (ex.: "amanhã às 10h").
Formato: {"tarefas":[{"titulo":"curto, em ${idiomaResposta()}","data":"AAAA-MM-DD" ou null,"hora":"HH:MM" ou null,"duracao":minutos,"energia":1 a 5,"notas":"detalhe, se houver"}]}`,
    conteudo.slice(0, 8000));
  return (Array.isArray(r.tarefas) ? r.tarefas : []).slice(0, 20).map((x: any) => ({
    titulo: String(x.titulo ?? '').slice(0, 120), data: okData(x.data) ? x.data : null, hora: okHora(x.hora) ? x.hora : null,
    duracao: Math.max(5, Math.min(600, Number(x.duracao) || 30)), energia: Math.max(0, Math.min(5, Math.round(Number(x.energia) || 2))), notas: String(x.notas ?? ''),
  })).filter((x: TarefaSugerida) => x.titulo.trim());
}

/** Sugere a categoria de cada lançamento do extrato. */
export async function categorizarExtrato(itens: { desc: string; valor: number }[], categorias: string[]): Promise<string[]> {
  const r = await perguntarJSON<any>(
    `Classifique lançamentos de extrato bancário. Categorias permitidas: ${JSON.stringify(categorias)}. Valores positivos são entradas ("entrada"), exceto resgates de investimento ("invest").
Formato: {"cats":["categoria do item 0","categoria do item 1",...]} na mesma ordem e quantidade dos itens.`,
    itens.slice(0, 200).map((x, i) => `${i}. ${x.desc} | ${x.valor}`).join('\n'));
  const cats: string[] = Array.isArray(r.cats) ? r.cats : [];
  return itens.map((_, i) => (categorias.includes(cats[i]) ? cats[i] : ''));
}

/** Lê uma foto ou PDF (nota fiscal, receita médica, voucher, recibo...) e preenche os campos de uma ficha.
 *  Só devolve o que conseguiu ler; o que não estiver no documento fica de fora. */
export async function lerFicha(nome: string, campos: Campo[], img: Imagem, comItens = false): Promise<{ c: Record<string, any>; itens: string[] }> {
  const usados = campos.filter(f => f.t !== 'ref' && f.t !== 'estrelas');
  const desc = usados.map(f => {
    const tipo = f.t === 'data' ? '"AAAA-MM-DD"' : f.t === 'hora' ? '"HH:MM"' : f.t === 'numero' || f.t === 'dinheiro' ? 'número (sem símbolo de moeda)'
      : f.t === 'sim' ? 'true ou false' : f.t === 'fuso' ? 'fuso IANA, ex.: "Europe/Lisbon"'
      : f.t === 'opcao' ? `um destes valores exatamente: ${JSON.stringify(f.op)}` : f.t === 'multi' ? `lista com valores destes: ${JSON.stringify(f.op)}` : 'texto';
    return `"${f.k}": ${tipo}  // ${f.r}${f.ia ? ` (${f.ia})` : ''}`;
  });
  const r = await perguntarJSON<any>(
    `Você lê fotos e PDFs (notas fiscais, receitas médicas, recibos, reservas, bilhetes, laudos, boletos, de qualquer país) e preenche a ficha "${nome}".
Hoje é ${today()}. Não invente: omita o campo ou use null quando a informação não estiver no documento. Textos em ${idiomaResposta()}, mas copie nomes, números e códigos como estão.
Formato JSON: {${desc.join(',\n')}${comItens ? ',\n"itens": ["lista de itens do documento, um por linha"]' : ''}}`,
    'Leia o documento da imagem e preencha a ficha.', [img]);
  const c: Record<string, any> = {};
  for (const f of usados) {
    const v = r?.[f.k];
    if (v === null || v === undefined || v === '') continue;
    switch (f.t) {
      case 'data': if (okData(v)) c[f.k] = v; break;
      case 'hora': if (okHora(v)) c[f.k] = v; break;
      case 'numero': case 'dinheiro': { const n = typeof v === 'number' ? v : Number(String(v).replace(/[^\d,.-]/g, '').replace(/\.(?=\d{3}\b)/g, '').replace(',', '.')); if (isFinite(n)) c[f.k] = Math.round(n * 100) / 100; break; }
      case 'sim': c[f.k] = v === true || v === 'true'; break;
      case 'opcao': if (f.op?.includes(v)) c[f.k] = v; break;
      case 'multi': if (Array.isArray(v)) { const l = v.filter(x => f.op?.includes(x)); if (l.length) c[f.k] = l; } break;
      case 'fuso': if (typeof v === 'string' && /^[A-Za-z_]+\/[A-Za-z_\/+-]+$/.test(v)) c[f.k] = v; break;
      default: c[f.k] = String(v).slice(0, f.t === 'longo' || f.t === 'cofre' ? 4000 : 200);
    }
  }
  const itens = comItens && Array.isArray(r?.itens) ? r.itens.map((x: any) => String(x).trim()).filter(Boolean).slice(0, 80) : [];
  return { c, itens };
}

export type ComprovanteLido = { desc?: string; valor?: number; data?: string; cat?: string };
/** Lê a foto (ou PDF) de um comprovante de pagamento, Pix, transferência ou recibo (usa o mesmo leitor das fichas).
 *  cats: categorias permitidas para o gasto (vazio = não classifica); entrada: dinheiro recebido. */
export async function lerComprovante(img: Imagem, cats: string[] = [], entrada = false): Promise<ComprovanteLido> {
  const campos: Campo[] = [
    { k: 'desc', r: 'Descrição', t: 'texto', ia: entrada ? 'nome curto de quem pagou e do que se trata' : 'nome curto de quem recebeu (loja, empresa ou pessoa) e do que foi pago' },
    { k: 'valor', r: 'Valor', t: 'dinheiro', ia: 'valor total efetivamente pago ou transferido' },
    { k: 'data', r: 'Data', t: 'data', ia: 'data do pagamento ou da transferência' },
    ...(cats.length && !entrada ? [{ k: 'cat', r: 'Categoria do gasto', t: 'opcao' as const, op: cats }] : []),
  ];
  const { c } = await lerFicha(entrada ? 'Comprovante de recebimento' : 'Comprovante de pagamento', campos, img);
  return { desc: c.desc, valor: typeof c.valor === 'number' && c.valor !== 0 ? Math.abs(c.valor) : undefined, data: c.data, cat: c.cat };
}
